import React, { useEffect, useState, useRef } from 'react';
import { fitRect } from "../helpers";
import { whenVideoReady } from '../mux';
import i18n from '../i18n';
import MuxVideoPlayer from './MuxVideoPlayer';

const FileEditorVideo = ({ video, setState }) => {

    const videoContainerRef = useRef(null);
    const videoRef = useRef(null);

    const [dimensions, setDimensions] = useState({
        width: 0,
        height: 0
    });

    // Mux hands us a playback ID before the asset has finished processing, but
    // the player errors out (and stays dead) if we mount it while Mux is still
    // preparing. So wait until the asset is actually ready before rendering it.
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        setIsReady(false);
        if (!video.muxPlaybackId) {
            return;
        }
        const controller = new AbortController();
        whenVideoReady(video, { signal: controller.signal })
            .then(() => setIsReady(true))
            .catch(() => { /* aborted, or no playback ID – leave the placeholder up */ });
        return () => controller.abort();
    }, [video.muxPlaybackId]);

    const onResize = () => {
        const { current: imageContainer } = videoContainerRef;
        const { width, height } = fitRect(video, imageContainer.getBoundingClientRect());
        setDimensions({ width, height });
    };

    useEffect(() => {
        window.addEventListener('resize', onResize);
        onResize();
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            userSelect: 'none'
        }}>
            <div ref={videoContainerRef} style={{
                display: 'flex',
                position: 'relative',
                flex: '1 1 100%',
                height: '100%',
                visibility: dimensions.width ? '' : 'hidden'
            }}>
                <div ref={videoRef} style={{ margin: 'auto', position: 'relative', backgroundColor: 'rgba(255, 255, 255, 0.1)', ...dimensions }}>
                    {dimensions.width && isReady ? (
                        <MuxVideoPlayer
                            playbackId={video.muxPlaybackId}
                            controls
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                objectFit: 'contain',
                                width: '100%',
                                height: '100%',
                                zIndex: 2
                            }}
                        />
                    ) : null}
                    {dimensions.width && !isReady ? (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            padding: '1rem',
                            color: 'rgba(255, 255, 255, 0.75)',
                            fontSize: '0.85rem',
                            zIndex: 2
                        }}>
                            <p>{i18n('This video is still being processed. It will start playing automatically once it’s ready.')}</p>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default FileEditorVideo;
